import * as React from 'react'
import { cva, cx, type VariantProps } from 'class-variance-authority'
import { type ZodAny, type ZodType } from 'zod/v4'

const checkboxVariants = cva(
  'size-4 rounded border-2 cursor-pointer focus:ring-primary-500 text-primary-500',
  {
    variants: {
      variant: {
        default: 'bg-white border-neutral-200',
        error: 'border-destructive',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export type CheckboxProps = React.ComponentProps<'input'> &
  VariantProps<typeof checkboxVariants> & {
    name: string
    register?: (
      name: string,
      RegisterOptions?,
    ) => { onChange; onBlur; name; ref } // https://react-hook-form.com/docs/useform/register
    validation?: ZodType<ZodAny>
    errors?: any
    variant?: 'default' | 'error'
    indeterminate?: boolean
  }

export const Checkbox = (props: CheckboxProps) => {
  const {
    name,
    register,
    validation,
    errors = {},
    className,
    indeterminate,
    ...rest
  } = props
  const variant = errors[name] ? 'error' : (props.variant ?? 'default')
  const valid = {}
  const registerProps = register?.(name, valid)

  const checkboxRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate || false
    }
  }, [indeterminate])

  return (
    <input
      type='checkbox'
      id={name}
      name={name}
      ref={checkboxRef}
      className={cx(checkboxVariants({ variant }), className)}
      {...registerProps}
      {...rest}
    />
  )
}
